<template>
  <LsModal
    :title="title"
    track-id="leads_hired_flow_modal"
    class="leads-hired-flow-modal--container"
  >
    <template #body>
      <div v-if="step === 'initial'" class="leads-hired-flow-modal--buttons-wrapper">
        <LsButton
          class="leads-hired-flow-modal--button"
          text="Yes, I hired"
          track-id="leads_hired_flow_modal-yes"
          :disabled="stillLookingButtonLoading || notHiringButtonLoading"
          @click="changeStep({ title: 'Which pro did you hire?', step: 'which_pro' })"
        />
        <LsButton
          class="leads-hired-flow-modal--button"
          text="I'm not hiring"
          track-id="leads_hired_flow_modal-no"
          :loading="notHiringButtonLoading"
          :disabled="stillLookingButtonLoading"
          test-id="not-hiring"
          @click="closeLead({ reason: 'not_hiring' })"
        />
        <LsButton
          class="leads-hired-flow-modal--button"
          text="I'm still looking"
          track-id="leads_hired_flow_modal-looking"
          :loading="stillLookingButtonLoading"
          :disabled="notHiringButtonLoading"
          test-id="still-looking"
          @click="stillLooking"
        />
      </div>
      <div v-else-if="step === 'which_pro'">
        <LsRadios
          v-model="contactId"
          track-id="leads_hired_flow_modal-which_pro"
          :options="leadContactsOptions"
          name="contactId"
          :validation="{ required: true }"
        />
      </div>
      <p v-else-if="step === 'thanks'" class="leads-hired-flow-modal--thanks">
        Come back if we can help solve any future jobs.
      </p>
    </template>
    <template v-if="step === 'which_pro'" #footer>
      <div class="leads-hired-flow-modal--submit-button-wrapper">
        <LsButton
          class="leads-hired-flow-modal--button"
          text="Submit"
          track-id="leads_hired_flow_modal-submit"
          :loading="customerLeads.loadingStatus.isUpdating"
          test-id="submit"
          @click="closeOrHire"
        />
      </div>
    </template>
  </LsModal>
</template>

<script>
import { LsButton, LsRadios, LsModal } from '@/components/common';
import { lsModalService, lsFilters, lsRouterHelpers } from 'ls-vue-common';
import _ from 'lodash';
import VueTypes from 'vue-types';
import { mapActions, mapState, mapGetters } from 'vuex';
import {
  CUSTOMER_LEADS__CLOSE_LEAD,
  CUSTOMER_LEADS__STILL_LOOKING,
  CUSTOMER_LEADS__HIRE_PRO,
  PROPERTIES__GET_FIRST_HIRED_PRO_NEEDING_DETAILS,
} from '@/store/types';
import { LeadsHiredDetailsModal } from '@/modals';
import { themedComponentMixin } from '@/mixins';

export default {
  name: 'LeadsHiredFlowModal',
  $_veeValidate: {
    validator: 'new',
  },
  components: {
    LsButton,
    LsRadios,
    LsModal,
  },
  mixins: [themedComponentMixin],
  props: {
    customerLead: VueTypes.shape({
      id: VueTypes.number.isRequired,
      customer_lead_contacts: VueTypes.arrayOf(
        VueTypes.shape({
          id: VueTypes.number.isRequired,
          contractor: VueTypes.shape({
            name: VueTypes.string.isRequired,
            city: VueTypes.string.isRequired,
            state: VueTypes.string.isRequired,
            user: VueTypes.shape({}).isRequired.loose,
          }).isRequired.loose,
        }).isRequired.loose
      ),
    }).isRequired.loose,
    customerLeadId: VueTypes.number.isRequired,
    propertyId: VueTypes.number.isRequired,
    showWhichProView: VueTypes.bool.def(false),
  },
  data() {
    let contactId = null;

    const customer_lead = lsRouterHelpers.getQuery(this.$route, 'customer_lead');
    const customer_lead_contact_id = _.toNumber(
      lsRouterHelpers.getQuery(this.$route, 'customer_lead_contact_id')
    );
    if (
      customer_lead === 'contact_hired' &&
      customer_lead_contact_id &&
      _.find(this.customerLead.customer_lead_contacts, { id: customer_lead_contact_id })
    ) {
      contactId = customer_lead_contact_id;
    }

    return {
      step: this.showWhichProView ? 'which_pro' : 'initial',
      title: this.showWhichProView
        ? 'Which pro did you hire?'
        : 'Did you hire a pro for your landscaping job?',
      contactId,
      notHiringButtonLoading: false,
      stillLookingButtonLoading: false,
    };
  },
  computed: {
    ...mapState(['customerLeads']),
    ...mapGetters([PROPERTIES__GET_FIRST_HIRED_PRO_NEEDING_DETAILS]),
    leadContactsOptions() {
      const { customer_lead_contacts } = this.customerLead;

      return _.concat(
        _.map(customer_lead_contacts, ({ id, contractor }) => {
          return {
            value: id,
            label: `${lsFilters.fullName({ user: contractor.user })}, ${contractor.name}`,
            subLabel: `${contractor.city}, ${_.toUpper(contractor.state)}`,
            boldLabel: true,
          };
        }),
        {
          value: 0,
          label: `Non-${this.brandName} Pro`,
          boldLabel: true,
        }
      );
    },
  },
  methods: {
    ...mapActions([
      CUSTOMER_LEADS__CLOSE_LEAD,
      CUSTOMER_LEADS__STILL_LOOKING,
      CUSTOMER_LEADS__HIRE_PRO,
    ]),
    changeStep({ step, title }) {
      this.step = step;
      this.title = title;
    },
    closeOrHire() {
      if (this.contactId === 0) {
        this.closeLead({ reason: 'non_lawnstarter' });
      } else {
        this.hirePro();
      }
    },
    async closeLead({ reason }) {
      this.notHiringButtonLoading = true;

      const { propertyId, customerLeadId } = this;

      await this[CUSTOMER_LEADS__CLOSE_LEAD]({
        customerLeadId,
        propertyId,
        onSuccess: this.onCloseLeadSuccess,
        reason,
      });
      this.notHiringButtonLoading = false;
    },
    async stillLooking() {
      this.stillLookingButtonLoading = true;
      const { propertyId, customerLeadId } = this;

      await this[CUSTOMER_LEADS__STILL_LOOKING]({
        customerLeadId,
        propertyId,
        onSuccess: this.close,
      });

      this.stillLookingButtonLoading = false;
    },
    async hirePro() {
      const valid = await this.$validator.validateAll();

      if (!valid) {
        return;
      }

      const { propertyId, customerLeadId, contactId } = this;

      await this[CUSTOMER_LEADS__HIRE_PRO]({
        customerLeadId,
        propertyId,
        contactId,
        onSuccess: this.onHireProSuccess,
      });
    },
    onCloseLeadSuccess() {
      this.changeStep({
        title: 'Thank you for letting us know',
        step: 'thanks',
      });
    },
    onHireProSuccess(response) {
      this.close();

      if (response) {
        const { data } = response;
        const { customerLead, propertyId, customerLeadId } = this;

        lsModalService.open({
          parent: this,
          component: LeadsHiredDetailsModal,
          width: 760,
          props: {
            customerLeadId,
            customerLeadContact: {
              id: data.id,
              status: data.status,
              contractor: {
                name: data.contractor.name,
                user: _.pick(data.contractor.user, ['first_name', 'last_name']),
              },
            },
            serviceName: _.map(customerLead.services, (service) => {
              return service.name;
            }).join(', '),
            propertyId,
            createdAt: customerLead.created_at,
          },
        });
      }
    },
    close() {
      this.$parent.close();
    },
  },
};
</script>

<style lang="scss">
.leads-hired-flow-modal--container {
  padding: 60px;

  .modal-body {
    padding: 20px 0;
  }

  .leads-hired-flow-modal--buttons-wrapper {
    padding-top: 30px;
  }

  .leads-hired-flow-modal--submit-button-wrapper {
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;

    @media (max-width: $desktop) {
      width: 100%;
    }
  }

  .leads-hired-flow-modal--button {
    width: auto;
    margin-right: 10px;

    @media (max-width: $desktop) {
      width: 100%;
      margin: 10px 0;
    }
  }

  .leads-hired-flow-modal--thanks {
    font-size: $size-4;
  }

  .modal-header {
    margin-bottom: 20px;
  }
}
.themed-app {
  .leads-hired-flow-modal--container {
    .leads-hired-flow-modal--thanks {
      font-size: $theme-txt-body-lg;
    }
  }
}
</style>
